import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, FileText, Briefcase, Code, BarChart3, Heart, TrendingUp,
  Loader2, Search, Palette, Users, GraduationCap, Shield, PenTool, DollarSign,
} from 'lucide-react';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';


const CATEGORY_ICONS = {
  Technology: Code,
  Marketing: TrendingUp,
  Management: Briefcase,
  Healthcare: Heart,
  Sales: BarChart3,
  Design: Palette,
  Finance: DollarSign,
  'Human Resources': Users,
  Creative: PenTool,
  Education: GraduationCap,
  Administrative: FileText,
};

const CATEGORY_COLORS = {
  Technology: 'text-[#002fa7] bg-[#002fa7]/10',
  Marketing: 'text-[#10b981] bg-[#10b981]/10',
  Management: 'text-[#7c3aed] bg-[#7c3aed]/10',
  Healthcare: 'text-[#e11d48] bg-[#e11d48]/10',
  Sales: 'text-[#f59e0b] bg-[#f59e0b]/10',
  Design: 'text-[#ec4899] bg-[#ec4899]/10',
  Finance: 'text-[#059669] bg-[#059669]/10',
  'Human Resources': 'text-[#6366f1] bg-[#6366f1]/10',
  Creative: 'text-[#d946ef] bg-[#d946ef]/10',
  Education: 'text-[#0891b2] bg-[#0891b2]/10',
  Administrative: 'text-[#64748b] bg-[#64748b]/10',
};

const fadeInUp = { opacity: 0, y: 20 };
const fadeInUpAnimate = { opacity: 1, y: 0 };

const TemplateCard = ({ template, onSelect, loading }) => {
  const Icon = CATEGORY_ICONS[template.category] || FileText;
  const colors = CATEGORY_COLORS[template.category] || 'text-[#002fa7] bg-[#002fa7]/10';

  return (
    <motion.div
      initial={fadeInUp}
      animate={fadeInUpAnimate}
      className="dashboard-card hover:border-[#002fa7] border-2 border-transparent transition-all cursor-pointer group hover:shadow-lg"
      data-testid={`template-card-${template.template_id}`}
      onClick={() => onSelect(template.template_id)}
    >
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 flex items-center justify-center ${colors}`}>
            <Icon size={20} />
          </div>
          <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 ${colors}`}>
            {template.category}
          </span>
        </div>
        <h3 className="font-satoshi text-lg font-bold text-[#0f1115] mb-2 group-hover:text-[#002fa7] transition-colors">
          {template.name}
        </h3>
        <p className="text-sm text-[#4b5563] leading-relaxed mb-4">
          {template.description}
        </p>
        <div className="flex items-center text-sm font-semibold text-[#002fa7]">
          {loading ? (
            <><Loader2 size={14} className="animate-spin mr-1" /> Loading...</>
          ) : (
            <><span>Use Template</span><ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" /></>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CategoryPill = ({ category, count, isActive, onClick }) => {
  const Icon = CATEGORY_ICONS[category] || FileText;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors whitespace-nowrap ${
        isActive
          ? 'bg-[#002fa7] text-white'
          : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] hover:text-[#002fa7]'
      }`}
      data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon size={14} />
      {category} ({count})
    </button>
  );
};

export default function TemplatesGallery() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API}/resumes/templates/gallery`, { withCredentials: true });
        setTemplates(res.data);
      } catch (err) {
        logger.error('Failed to fetch templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const categories = useMemo(() => {
    const counts = {};
    templates.forEach(t => { counts[t.category] = (counts[t.category] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      const matchesCategory = activeCategory === 'All' || t.category === activeCategory;
      const matchesSearch = !searchTerm ||
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.target_job.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [templates, activeCategory, searchTerm]);

  const handleSelect = async (templateId) => {
    setSelectingId(templateId);
    try {
      const res = await axios.get(`${API}/resumes/templates/${templateId}`, { withCredentials: true });
      const tpl = res.data;
      navigate('/resume/new', {
        state: {
          fromTemplate: true,
          title: `${tpl.name} Resume`,
          content: tpl.content,
          target_job: tpl.target_job,
        },
      });
    } catch {
      setSelectingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="templates-gallery-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">
                Resume Templates
              </h1>
              <p className="text-[#4b5563] mt-1">
                {templates.length} professionally optimized templates across {categories.length} industries
              </p>
            </div>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" size={18} />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2.5 border border-[#e5e7eb] bg-white text-sm w-64 focus:border-[#002fa7] transition-colors"
                data-testid="template-search"
              />
            </div>
          </div>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
          <button
            onClick={() => setActiveCategory('All')}
            className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold transition-colors ${
              activeCategory === 'All'
                ? 'bg-[#002fa7] text-white'
                : 'bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] hover:text-[#002fa7]'
            }`}
            data-testid="category-all"
          >
            All ({templates.length})
          </button>
          {categories.map(([cat, count]) => (
            <CategoryPill
              key={cat}
              category={cat}
              count={count}
              isActive={activeCategory === cat}
              onClick={() => setActiveCategory(cat)}
            />
          ))}
        </div>

        {/* Mobile search */}
        <div className="sm:hidden mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b7280]" size={18} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-[#e5e7eb] bg-white text-sm focus:border-[#002fa7] transition-colors"
            />
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={`skeleton-${i}`} className="dashboard-card p-6 animate-pulse">
                <div className="h-10 w-10 bg-[#e5e7eb] mb-4"></div>
                <div className="h-6 bg-[#e5e7eb] w-3/4 mb-3"></div>
                <div className="h-4 bg-[#e5e7eb] w-full mb-2"></div>
                <div className="h-4 bg-[#e5e7eb] w-2/3"></div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredTemplates.length === 0 && (
          <div className="dashboard-card p-12 text-center">
            <Search size={48} className="mx-auto mb-4 text-[#d1d5db]" />
            <p className="font-semibold text-[#0f1115]">No templates found</p>
            <p className="text-sm text-[#6b7280] mt-1">Try a different search term or category</p>
            <button onClick={() => { setSearchTerm(''); setActiveCategory('All'); }} className="mt-4 text-sm text-[#002fa7] font-semibold hover:underline">
              Clear filters
            </button>
          </div>
        )}

        {!loading && filteredTemplates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.template_id}
                template={tpl}
                onSelect={handleSelect}
                loading={selectingId === tpl.template_id}
              />
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <p className="text-sm text-[#6b7280] mb-3">
            Don't see your role? Start from scratch with your own content.
          </p>
          <Link to="/resume/new" className="btn-outline inline-flex items-center gap-2" data-testid="create-blank">
            <FileText size={16} />
            Create Blank Resume
          </Link>
        </div>
      </div>
    </div>
  );
}
